import { ScenarioFileRow } from "@custom-types/graphs"
import {
  LOCAL_SCENARIO_HASH,
  getUnpackedGraph,
  saveGraphBase64ToIndexedDb,
} from "@features/graph/state/indexedDBHelpers"
import { GraphConverter } from "@store/services/converter/graphConverter"
import { getTableRowsFromCsvContent, rowsToCsvContent } from "@utils/file"
import logger from "@utils/logger"
import { blobToBase64 } from "base64-blob"
import { enqueueSnackbar } from "notistack"
import { useCallback, useEffect, useRef, useState } from "react"

export const useLocalGraphScenario = (graphKey: string) => {
  const [scenario, setScenario] = useState<string>()

  // tracks the graphKey the latest request was made for, so a stale response
  // (e.g. from a request for a previous graphKey) doesn't overwrite newer state
  const requestedGraphKeyRef = useRef<string>()

  useEffect(() => {
    if (!graphKey) {
      setScenario(undefined)
      return
    }

    requestedGraphKeyRef.current = graphKey

    getUnpackedGraph(graphKey).then((localGraph) => {
      if (requestedGraphKeyRef.current !== graphKey) return
      setScenario(localGraph?.scenario)
    })
  }, [graphKey])

  const updateScenario = useCallback(
    async (newRow: ScenarioFileRow) => {
      const localGraph = await getUnpackedGraph(graphKey)
      if (!localGraph) {
        logger.error(`Graph with key ${graphKey} not found in IndexedDB`)
        enqueueSnackbar("Something went wrong while saving the mnemonic data.")
        return
      }

      const scenarioRows = getTableRowsFromCsvContent(
        localGraph.scenario ?? "Date,Mnem,Value,Scenario\n",
      )

      const existingRowIndex = scenarioRows.findIndex(
        (row) =>
          row.Date === newRow.Date && row.Mnem === newRow.Mnem && row.Scenario === newRow.Scenario,
      )
      if (existingRowIndex !== -1) {
        scenarioRows[existingRowIndex] = newRow
      } else {
        scenarioRows.push(newRow)
      }

      localGraph.scenario = rowsToCsvContent(scenarioRows)
      const blob = await GraphConverter.packZip(localGraph)
      const base64 = await blobToBase64(blob)

      await saveGraphBase64ToIndexedDb(graphKey, base64, LOCAL_SCENARIO_HASH, true)

      setScenario(localGraph.scenario)
    },
    [graphKey],
  )

  return { scenario, updateScenario }
}
